import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { NavLink } from "react-router-dom";
import { useHistory } from "react-router-dom";
import axios from "../../../helper/axios";
import { Helmet } from "react-helmet";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../../App.css";
import { enc, SHA256 } from "crypto-js";
import * as Yup from "yup";
import {
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
  Alert,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { MdAccountCircle } from "react-icons/md";

const initialValues = {
  username: "",
  password: "",
};

// Validation Schema
const loginValidationSchema = Yup.object({
  username: Yup.string()
    .required("Username is required")
    .min(3, "Username must be at least 3 characters"),
  password: Yup.string()
    .required("Password is required")
    .min(6, "Password must be at least 6 characters"),
});

const Login = () => {
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const history = useHistory();

  useEffect(() => {
    const jwtToken = localStorage.getItem("jwtToken");
    if (jwtToken) {
      // Perform logout action
      localStorage.removeItem("jwtToken");
      history.push("/login");
    }
  }, []);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const formik = useFormik({
    initialValues,
    validationSchema: loginValidationSchema,
    onSubmit: async (values, actions) => {
      try {
        // Clear previous errors
        setError("");
        setSuccess(false);

        const hashedPassword = SHA256(values.password).toString(enc.Hex);

        const response = await axios.post("/AdminLogin", {
          ...values,
          password: hashedPassword,
        });

        if (response.data.success === true) {
          setSuccess(true);
          setError("");
          localStorage.setItem("jwtToken", response.data.token);

          // Small delay to show success message
          setTimeout(() => {
            history.push("/dashboard");
          }, 500);
        } else {
          // Handle specific error from backend
          setError(
            response.data.message ||
              "Invalid username or password. Please try again."
          );
          setSuccess(false);
        }
      } catch (err) {
        // Handle different types of errors
        let errorMessage = "An error occurred. Please try again.";

        if (err.response) {
          // Server responded with error
          if (err.response.status === 401) {
            errorMessage =
              "Invalid username or password. Please check your credentials.";
          } else if (err.response.status === 403) {
            errorMessage = "Access denied. Please contact administrator.";
          } else if (err.response.status === 404) {
            errorMessage = "Login service not found. Please contact support.";
          } else if (err.response.status === 500) {
            errorMessage = "Server error. Please try again later.";
          } else if (err.response.data?.message) {
            errorMessage = err.response.data.message;
          } else if (err.response.data?.detail) {
            errorMessage = err.response.data.detail;
          }
        } else if (err.request) {
          // Request made but no response
          errorMessage =
            "Network error. Please check your internet connection.";
        }

        setError(errorMessage);
        setSuccess(false);
        console.error("Login error:", err);
      } finally {
        actions.setSubmitting(false);
      }
    },
  });

  return (
    <div className="container1">
      <div className="image-container1">
        <img src="doctor.svg" alt="Login" className="login-image" />
      </div>
      <div className="form-container1">
        <Typography variant="h5" gutterBottom>
          <img className="logo" src="logo-aarogya.png" alt="Aarogya Logo" />
        </Typography>

        {/* Success and Error Messages */}
        <div style={{ marginBottom: "1rem" }}>
          {success && (
            <Alert severity="success">
              Login successful! Redirecting to dashboard...
            </Alert>
          )}
          {error && (
            <Alert severity="error" onClose={() => setError("")}>
              {error}
            </Alert>
          )}
        </div>

        <form onSubmit={formik.handleSubmit}>
          <div className="text-field">
            <TextField
              fullWidth
              label="Enter your name"
              variant="outlined"
              type="text"
              autoComplete="off"
              name="username"
              id="username"
              placeholder="Enter Your Username"
              value={formik.values.username}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.username && Boolean(formik.errors.username)}
              helperText={formik.touched.username && formik.errors.username}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton>
                      <MdAccountCircle />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ width: "25vw" }}
            />
          </div>

          <div className="text-field">
            <TextField
              fullWidth
              label="Enter your password"
              type={showPassword ? "text" : "password"}
              autoComplete="off"
              name="password"
              id="password"
              placeholder="Enter Password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowPassword}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ width: "25vw" }}
            />
          </div>

          <div className="remember-me">
            <FormControlLabel
              control={<Checkbox color="primary" />}
              label="Remember Me"
            />
          </div>

          <Button
            className="submit-button"
            variant="contained"
            color="primary"
            fullWidth
            type="submit"
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? "Logging in..." : "Login"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Login;
